import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react'
import {
  SOCIAL_DEFAULT_RETENTION_DAYS,
  SOCIAL_FIRST_IMPORT_DAYS,
} from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { Heading } from '@/components/ui/Heading'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type {
  RequiredField,
  SocialTrackerDialogProps,
} from '@/types/components/social-tracker-dialog'
import { SocialTrackerTopicsStep } from './SocialTrackerTopicsStep'
import { SocialTrackerSettingsStep } from './SocialTrackerSettingsStep'
import { useSocialTrackerWizard } from './useSocialTrackerWizard'

/** Opening or changing source starts a new session; parent rerenders never reset edits. */
export function SocialTrackerDialog(props: SocialTrackerDialogProps) {
  if (!props.open) return null
  const source = props.tracker
    ? props.tracker.token
    : props.initialProposal
      ? JSON.stringify(props.initialProposal)
      : 'new'
  return <TrackerWizardSession key={source} {...props} />
}

function TrackerWizardSession(props: SocialTrackerDialogProps) {
  const { t } = useTranslation('app')
  const wizard = useSocialTrackerWizard(props)
  const { form, drafts, step, missing, updateField, updateDraft, isSaving } =
    wizard
  const headingRef = useRef<HTMLHeadingElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 })
    headingRef.current?.focus()
  }, [step, wizard.isPreparing])
  function fieldError(field: RequiredField) {
    return missing.includes(field) ? (
      <Text
        as="span"
        size="xs"
        className="text-destructive"
        role="alert"
        id={`social-tracker-${field}-error`}
        data-testid={`social-tracker-${field}-error`}
      >
        {t(
          field === 'keywords'
            ? 'socialListening.wizard.topicsRequired'
            : 'socialListening.form.required',
        )}
      </Text>
    ) : null
  }
  const stepProps = {
    form,
    drafts,
    missing,
    updateField,
    updateDraft,
    fieldError,
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!isSaving) props.onOpenChange(open)
      }}
    >
      <DialogContent
        hideClose={isSaving}
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[720px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[720px] sm:p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          headingRef.current?.focus()
        }}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="flex shrink-0 flex-col gap-5 border-b border-border px-5 pb-5 pt-6 sm:px-8 sm:pt-8">
          <DialogTitle className="pr-8">
            {t(
              props.tracker
                ? 'socialListening.form.editTitle'
                : 'socialListening.wizard.newTracker',
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('socialListening.wizard.description')}
          </DialogDescription>
          <ol
            className="flex items-center gap-3 text-sm"
            aria-label={t('socialListening.wizard.steps')}
          >
            {(['topics', 'settings'] as const).map((value, index) => (
              <li
                key={value}
                className="flex items-center gap-3"
                aria-current={step === value ? 'step' : undefined}
              >
                {index > 0 ? (
                  <span className="h-px w-8 bg-border" aria-hidden="true" />
                ) : null}
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full border text-xs',
                    step === value
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border text-muted-foreground',
                  )}
                >
                  {value === 'topics' && step === 'settings' ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    index + 1
                  )}
                </span>
                <span
                  className={
                    step === value ? 'text-foreground' : 'text-muted-foreground'
                  }
                >
                  {t(`socialListening.wizard.${value}`)}
                </span>
              </li>
            ))}
          </ol>
        </div>
        <form
          className="flex min-h-0 flex-col"
          data-testid="social-tracker-form"
          noValidate
          aria-busy={isSaving || wizard.isPreparing}
          onSubmit={(event) => {
            event.preventDefault()
            if (step === 'topics') void wizard.goToSettings()
            else void wizard.save()
          }}
        >
          <div
            ref={bodyRef}
            className="flex min-h-0 flex-col gap-5 overflow-y-auto overscroll-contain px-5 py-6 sm:px-8"
          >
            <div className="flex flex-col gap-1.5">
              <Heading
                ref={headingRef}
                level={2}
                tabIndex={-1}
                className="text-xl outline-none"
              >
                {t(
                  wizard.isPreparing
                    ? 'socialListening.wizard.preparingTitle'
                    : step === 'topics'
                      ? 'socialListening.wizard.topicsTitle'
                      : 'socialListening.wizard.settingsTitle',
                )}
              </Heading>
              <Text size="sm" color="muted">
                {t(
                  wizard.isPreparing
                    ? 'socialListening.wizard.preparingDescription'
                    : step === 'topics'
                      ? 'socialListening.wizard.topicsDescription'
                      : 'socialListening.wizard.settingsDescription',
                )}
              </Text>
            </div>
            {wizard.isPreparing ? (
              <div
                role="status"
                aria-label={t('socialListening.wizard.preparing')}
                data-testid="social-tracker-preparing-step"
                className="flex min-h-[240px] flex-col items-center justify-center gap-6 py-6"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Loader2
                    className="h-6 w-6 motion-safe:animate-spin"
                    aria-hidden="true"
                  />
                </div>
                <div
                  className="flex w-full max-w-sm flex-col gap-4"
                  aria-hidden="true"
                >
                  <Skeleton className="h-11 w-full motion-reduce:animate-none" />
                  <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-10 motion-reduce:animate-none" />
                    <Skeleton className="h-10 motion-reduce:animate-none" />
                  </div>
                  <Skeleton className="h-8 w-2/3 motion-reduce:animate-none" />
                </div>
              </div>
            ) : (
              <fieldset disabled={isSaving} className="min-w-0">
                {step === 'topics' ? (
                  <SocialTrackerTopicsStep
                    {...stepProps}
                    activeKeyword={wizard.activeKeyword}
                    onActiveKeywordChange={wizard.setActiveKeyword}
                    isBusy={isSaving || wizard.isPreparing}
                  />
                ) : (
                  <SocialTrackerSettingsStep
                    {...stepProps}
                    isEditing={Boolean(props.tracker)}
                    onEditTopics={wizard.goToTopics}
                  />
                )}
              </fieldset>
            )}
            {wizard.error ? (
              <Text
                size="sm"
                className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-destructive"
                role="alert"
                data-testid="social-tracker-error"
              >
                {wizard.error}
              </Text>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col gap-4 border-t border-border bg-background px-5 py-4 sm:px-8">
            {step === 'settings' ? (
              <Text
                size="xs"
                color="muted"
                data-testid="social-tracker-form-settings"
              >
                {t('socialListening.form.description', {
                  firstImportDays: SOCIAL_FIRST_IMPORT_DAYS,
                  retentionDays:
                    props.tracker?.retentionDays ??
                    SOCIAL_DEFAULT_RETENTION_DAYS,
                })}
              </Text>
            ) : null}
            <div
              className={cn(
                'flex items-center gap-3',
                step === 'topics' ? 'justify-end' : 'justify-between',
              )}
            >
              <Button
                type="button"
                variant="ghost"
                disabled={isSaving}
                onClick={() =>
                  step === 'topics'
                    ? props.onOpenChange(false)
                    : wizard.goToTopics()
                }
                data-testid={
                  step === 'topics'
                    ? 'social-tracker-cancel'
                    : 'social-tracker-back'
                }
              >
                {step === 'settings' ? (
                  <ArrowLeft className="mr-2 h-4 w-4" />
                ) : null}
                {t(
                  step === 'topics'
                    ? 'socialListening.form.cancel'
                    : 'socialListening.wizard.back',
                )}
              </Button>
              <Button
                type="submit"
                variant="cta"
                disabled={isSaving || wizard.isPreparing}
                data-testid={
                  step === 'topics'
                    ? 'social-tracker-continue'
                    : 'social-tracker-submit'
                }
              >
                {isSaving || wizard.isPreparing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {t(
                  isSaving
                    ? 'socialListening.form.saving'
                    : wizard.isPreparing
                      ? 'socialListening.wizard.preparing'
                      : step === 'topics'
                        ? wizard.preparationFailed
                          ? 'socialListening.wizard.retry'
                          : 'socialListening.wizard.continue'
                        : props.tracker
                          ? 'socialListening.form.save'
                          : 'socialListening.wizard.createTracker',
                )}
                {step === 'topics' && !wizard.isPreparing ? (
                  <ArrowRight className="ml-2 h-4 w-4" />
                ) : null}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
